import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import CheckCircle from "@mui/icons-material/CheckCircle";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import Replay from "@mui/icons-material/Replay";
import { List, ListItem, ListItemIcon, ListItemText, Paper, Typography } from "@mui/material";
import {
  ActivityStatus,
  CustomWorkDetailFieldsFragment,
  CustomWorkHistoryKind,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";

type HistoryEntry = CustomWorkDetailFieldsFragment["history"][number];

function entryIcon(entry: HistoryEntry) {
  if (entry.kind === CustomWorkHistoryKind.StepAdded) {
    return <AddCircleOutline color="action" fontSize="small" />;
  }
  if (entry.status === ActivityStatus.Completed || entry.status === ActivityStatus.StaffCompleted) {
    return <CheckCircle color="success" fontSize="small" />;
  }
  if (entry.status === ActivityStatus.Skipped) {
    return <RemoveCircleOutline color="disabled" fontSize="small" />;
  }
  return <Replay color="action" fontSize="small" />;
}

function entryText(entry: HistoryEntry): string {
  if (entry.kind === CustomWorkHistoryKind.StepAdded) {
    return `Step "${entry.stepDescription}" added`;
  }
  if (entry.status === ActivityStatus.NotStarted) {
    return `Step "${entry.stepDescription}" reopened`;
  }
  if (entry.status === ActivityStatus.Skipped) {
    return `Step "${entry.stepDescription}" skipped`;
  }
  if (entry.status === ActivityStatus.Completed || entry.status === ActivityStatus.StaffCompleted) {
    return `Step "${entry.stepDescription}" completed`;
  }
  return `Step "${entry.stepDescription}" moved to ${entry.status ?? "unknown"}`;
}

/**
 * The audit trail, newest first: every step added and every status change —
 * including undos — with who did it and when. Nothing here is ever erased;
 * an undo appends its own entry.
 */
export function WorkHistoryPanel({ history }: { history: HistoryEntry[] }) {
  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Typography variant="h6" sx={{ mb: 1 }}>
        History
      </Typography>
      {history.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing yet — step changes will be recorded here, with who and when.
        </Typography>
      ) : (
        <List dense disablePadding>
          {history.map((entry) => (
            <ListItem key={entry.id} disableGutters>
              <ListItemIcon sx={{ minWidth: 32 }}>{entryIcon(entry)}</ListItemIcon>
              <ListItemText
                primary={entryText(entry)}
                secondary={[
                  entry.actorEmail ?? undefined,
                  DateTime.fromISO(entry.time).toLocaleString(DateTime.DATETIME_MED),
                ]
                  .filter(Boolean)
                  .join(" · ")}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Paper>
  );
}
